<template>
  <ProfileLayout>
    <template #page-header>
      <span>{{ $t("ordersHistory") }}</span>
    </template>
    <template #default>
      <template v-if="isLoading || orders.length">
        <div class="mt-12">
          <img
            v-if="isLoading"
            src="/images/loader.gif"
            alt="Loader"
            class="mx-auto h-10 w-10 object-contain grayscale"
          />
          <template v-else>
            <ProfileOrderCard
              v-for="order in orders"
              :key="order.id"
              :order="order"
            />
          </template>
        </div>
        <div v-if="isPaginationVisible" class="mb-7 mt-9.5 flex justify-center">
          <UiPagination
            v-model="pages.current"
            :total-pages="pages.total"
            @update:model-value="getOrders"
          />
        </div>
      </template>
      <div v-else class="mt-12">
        <BlocksBlockRichText
          v-if="ordersEmptyStateData"
          :data="ordersEmptyStateData as RichTextModuleData"
        />
      </div>
    </template>
  </ProfileLayout>
</template>

<script setup lang="ts">
import { OrderData, RichTextModuleData } from "ecom-types";
import { FetchError } from "ofetch";

const { $api } = useNuxtApp();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const generalStore = useGeneralStore();
const { openSystemDialog } = useSystemDialogs();
const localePath = useLocalePathPolyfill();
const { visitEvent } = useEvents();

const pages = reactive({ current: 1, total: 0, count: 5 });
const orders = ref<OrderData[]>([]);
const isLoading = ref(false);

const { data: apiData } = await useAsyncData("orders-empty-state", () => {
  return $api.blocks.getBlock("orders-empty-state");
});

const ordersEmptyStateData = computed(() => {
  return apiData.value?.data?.currentTranslation?.content;
});

const isPaginationVisible = computed(
  () => orders.value?.length && pages.total > 1 && !isLoading.value,
);

watch(
  () => generalStore.isAuthenticated,
  (val) => {
    if (val) {
      getOrders();
    }
  },
);

onMounted(() => {
  visitEvent();
});

await getData();

async function getOrders() {
  if (process.client) {
    window.scrollTo({ top: 0 });
  }
  isLoading.value = true;

  try {
    const data = await $api.orders.getOrders({
      page: pages.current,
      count: pages.count,
    });
    orders.value = (data?.data as OrderData[]) || [];
    pages.total = data?.meta?.last_page || 0;
  } catch (error) {
    console.log("error - ", error);
  } finally {
    isLoading.value = false;
  }
}

async function getGuestOrder() {
  try {
    const data = await $api.orders.getTrackingOrder({
      email: route.query.email as string,
      number: route.query.number as string,
      billing_zip: route.query.billing_zip as string,
    });
    orders.value = [data.data as OrderData];
  } catch (error) {
    console.log(error);
    if (error instanceof FetchError) {
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: error?.data?.errors?.[0]?.message || "Wrong data",
      });
    }
  }
}

async function getData() {
  if (generalStore.isAuthenticated) {
    await getOrders();
  } else if (generalStore.guestOrderInfo) {
    orders.value = [generalStore.guestOrderInfo as OrderData];
  } else if (
    route.query.email &&
    route.query.number &&
    route.query.billing_zip
  ) {
    await getGuestOrder();
  } else {
    await router.push(localePath("/track-order"));
  }
}
</script>
